import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { ToastProvider } from '../components/Toast';
import { Spinner } from '../components/ui/Spinner';
import { api, ApiError, apiTryGet } from '../lib/api';
import { type AuthedUser, deriveAuthedUser, type Me } from '../lib/auth-types';
import { LoginPage } from '../pages/LoginPage';
import { type AppLinkId, appLinkLabel, getAppLinks, isAppLinkId } from './appLinks';
import { ToolsTopbar } from './ToolsTopbar';
import { ToolsSettings } from './ToolsSettings';
import { sanitizeNextPath } from '../../shared/sanitize-next-path';

/**
 * ToolsApp — Auth-Shell des schlanken tools-Clients (tools.greenforce.com).
 *
 * Vorlage = TransferApp.tsx (checking/unauthed/authed-Tristate + mustChangePassword-
 * Forced-Screen). Getrimmt: EAGER Login/Launchpad, kein Sidebar/Layout, kein
 * Onboarding. Auth läuft gegen das geteilte `users`-Verzeichnis (eigenes Cookie/
 * Secret durch den getrennten tools-Prozess + .env.tools, nicht hier).
 *
 * P1b: authed + Kachel-Klick = SSO-Handoff (Form-POST an den Mint).
 * P1c (Must 2): tools bekommt Pfad-Routing (wouter: `/` Launchpad, `/login`;
 * unbekannt → `/`) UND einen **automatischen** Deep-Link-Handoff. Ein von
 * DAM/Transfer redirecteter User landet auf `/login?app=<id>&next=<pfad>`; die
 * Query-Params sind der Contract (pfad-unabhängig geparst, D-P1c-5). Ist der User
 * (nach) eingeloggt und sind die Params valide, mintet ToolsApp automatisch den
 * P1b-Handoff → der User landet exakt auf seiner Deep-URL. Der Kachel-Klick-Flow
 * bleibt parallel funktionsfähig.
 */
type AuthState =
  | { status: 'checking' }
  | { status: 'unauthed' }
  | { status: 'authed'; user: AuthedUser };

/**
 * Handoff-Params aus der URL (Must 2): `app` gegen die Client-Whitelist
 * validiert (D-P1c-6 Client-Stufe), `next` via shared `sanitizeNextPath`
 * (Open-Redirect). `app=null` → kein Auto-Handoff (P1b-Verhalten).
 */
function parseHandoffParams(search: string): { app: AppLinkId | null; next: string } {
  const params = new URLSearchParams(search);
  const rawApp = params.get('app');
  return {
    app: isAppLinkId(rawApp) ? rawApp : null,
    next: sanitizeNextPath(params.get('next')),
  };
}

/**
 * Globaler Logout (PR-C, Codex-Findings R1 + R2).
 *
 * DAM/Transfer haben nach dem Cutover keinen eigenen Login mehr — ihr „Abmelden"
 * löscht nur das App-Cookie und schickt den User hierher. Erst hier stirbt die
 * IdP-Session; ohne diesen Schritt liefe der User in den Auto-Handoff und wäre
 * sofort wieder eingeloggt.
 *
 * **Bei Fehlschlag wird NICHT weiternavigiert** (Codex-R2): ein Netzwerk-/5xx-
 * Fehler lässt das HttpOnly-Cookie stehen. Würden wir trotzdem auf `/login`
 * gehen, authentifiziert `/me` den User dort sofort wieder und er landet auf dem
 * Launchpad — mit dem festen Eindruck, abgemeldet zu sein. Ein ehrlicher
 * Fehlerzustand mit „Erneut versuchen" ist die einzig vertretbare Variante;
 * navigiert wird ausschliesslich nach bestätigtem Cookie-Clear.
 *
 * Nach Erfolg `location.replace('/login')` statt eines State-Wechsels: die URL
 * `/logout` darf nicht stehen bleiben, sonst löst der Post-Login-`reload()`
 * direkt den nächsten Logout aus.
 *
 * **Läuft unabhängig vom `/me`-Ergebnis** (Codex-R3): `apiTryGet` mappt JEDEN
 * Fehler auf `null` — ein 5xx oder Timeout bei gültigem Cookie sähe also aus wie
 * „nicht angemeldet". Würden wir uns darauf verlassen, übersprängen wir den
 * Logout und liessen die IdP-Session offen. Der POST ist idempotent; nur ein
 * echtes 401 heisst „war schon abgemeldet".
 */
function GlobalLogout() {
  const ranRef = useRef(false);
  const [failed, setFailed] = useState(false);

  const run = useCallback(async () => {
    setFailed(false);
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      // 401 = es gab nichts zu beenden (Session bereits abgelaufen/weg). Das ist
      // der Ziel-Zustand, kein Fehler.
      if (err instanceof ApiError && err.status === 401) {
        window.location.replace('/login');
        return;
      }
      console.error('[auth] tools logout failed:', err);
      setFailed(true);
      return;
    }
    window.location.replace('/login');
  }, []);

  useEffect(() => {
    if (ranRef.current) return; // StrictMode-Doppel-Effekt
    ranRef.current = true;
    void run();
  }, [run]);

  if (failed) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 bg-brand-cream p-6">
        <p className="text-sm text-brand-green-700">
          Abmelden fehlgeschlagen — du bist weiterhin angemeldet.
        </p>
        <button
          type="button"
          onClick={() => void run()}
          className="rounded-lg bg-brand-green-700 px-4 py-2 text-sm font-semibold text-brand-cream transition-colors hover:bg-brand-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
        >
          Erneut versuchen
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-5 bg-brand-cream p-6">
      <Spinner size="lg" className="text-brand-green-700" />
      <p className="text-sm text-brand-green-600">Abmelden …</p>
    </main>
  );
}

/**
 * Auto-Handoff (Must 2 (a)/(d)): full-page Form-POST an den Mint (`/api/sso/handoff`)
 * — KEIN fetch, weil der Mint mit der navigierenden Auto-Submit-Seite antwortet
 * (P1b-Design, D-P1c-5). `requestSubmit()` im Effect, Once-Ref gegen den
 * StrictMode-Doppel-Effekt (genau EIN Mint). `next` reist als unsigned Form-Field,
 * der Consumer re-sanitized (P1b). Sichtbarer „Weiterleitung …"-Zustand +
 * manueller Fallback-Button.
 */
function AutoHandoff({ app, next }: { app: AppLinkId; next: string }) {
  const formRef = useRef<HTMLFormElement>(null);
  const submittedRef = useRef(false);
  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    formRef.current?.requestSubmit();
  }, []);
  const label = appLinkLabel(app);
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-5 bg-brand-cream p-6">
      <Spinner size="lg" className="text-brand-green-700" />
      <p className="text-sm text-brand-green-600">Weiterleitung zu {label} …</p>
      <form ref={formRef} method="POST" action="/api/sso/handoff">
        <input type="hidden" name="target" value={app} />
        <input type="hidden" name="next" value={next} />
        <button
          type="submit"
          className="rounded-lg bg-brand-green-700 px-4 py-2 text-sm font-semibold text-brand-cream transition-colors hover:bg-brand-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
        >
          Weiter zu {label}
        </button>
      </form>
    </main>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream">
      <Spinner size="lg" className="text-brand-green-700" />
      <span className="sr-only">Lädt…</span>
    </div>
  );
}

function Launchpad({ user, onLogout }: { user: AuthedUser; onLogout: () => void }) {
  const links = getAppLinks();
  return (
    <div className="min-h-screen bg-brand-cream">
      <ToolsTopbar user={user} onLogout={onLogout} />

      <main className="mx-auto w-full max-w-6xl px-6 py-12">
        <nav aria-label="Anwendungen">
          <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {links.map((link) => (
              <li key={link.id}>
                {/* SSO-Handoff (P1b): echter Form-POST an den tools-Mint (kein
                    <a href> mehr). `target` = link.id (server-seitige Whitelist),
                    volle Page-Navigation → Mint rendert die Auto-Submit-Seite. */}
                <form method="POST" action="/api/sso/handoff">
                  <input type="hidden" name="target" value={link.id} />
                  {/* Card-div trägt die bisherigen <a>-Klassen (Optik 1:1) +
                      cursor-pointer + onClick → requestSubmit: die GANZE Card
                      bleibt klickbar. Klicks direkt auf den Submit-Button lösen
                      die native Submission aus (Guard verhindert Doppel-Submit). */}
                  <div
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      e.currentTarget.closest('form')?.requestSubmit();
                    }}
                    className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white shadow-md transition hover:shadow-lg"
                  >
                    <img
                      src={link.image}
                      alt=""
                      loading="lazy"
                      className="aspect-[16/9] w-full border-b border-ink-100 object-cover object-top"
                    />
                    <div className="flex flex-1 flex-col p-8">
                      <span className="block text-xl font-semibold text-brand-green-700">
                        {link.label}
                      </span>
                      <span className="mt-2 block text-sm text-brand-green-600">
                        {link.description}
                      </span>
                      {/* Spacer schiebt den CTA nach unten → Buttons schließen trotz
                          unterschiedlich langer Beschreibung bündig ab; min-h sichert
                          den Mindestabstand. */}
                      <div className="min-h-6 flex-1" aria-hidden="true" />
                      {/* CTA — jetzt das ECHTE interaktive Element (Submit-Button):
                          Fokus-/Keyboard-Ziel (Tab + Enter), a11y-Gewinn ggü. dem
                          früheren rein-visuellen <span>. group-hover animiert den Pfeil. */}
                      <button
                        type="submit"
                        className="inline-flex items-center gap-1.5 self-start rounded-lg bg-brand-green-700 px-4 py-2 text-sm font-semibold text-brand-cream transition-colors group-hover:bg-brand-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
                      >
                        Öffnen
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden="true"
                          className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        >
                          <path
                            d="M4 10h11M11 5.5 15.5 10 11 14.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </li>
            ))}
          </ul>
        </nav>
      </main>
    </div>
  );
}

export function ToolsApp() {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });
  const [location] = useLocation();

  // Handoff-Params sind Query-basiert (pfad-unabhängig, D-P1c-5) → einmal beim
  // Mount parsen. Nach Passwort-Login bringt reload() sie zurück (URL bleibt).
  const handoff = useMemo(() => parseHandoffParams(window.location.search), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await apiTryGet<Me>('/api/auth/me');
      if (cancelled) return;
      setAuth(
        me ? { status: 'authed', user: deriveAuthedUser(me) } : { status: 'unauthed' },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.status === 'checking') return <LoadingScreen />;

  // /logout steht VOR allen Auth-Gates (Codex-R2). Sonst greift bei fehlender
  // Session der unauthed-Branch (LoginPage AUF /logout → der Post-Login-reload
  // löscht die frische Session sofort wieder) bzw. bei mustChangePassword der
  // Passwort-Zwang — statt dass schlicht abgemeldet wird. Abmelden muss aus
  // JEDEM Auth-Zustand funktionieren.
  //
  // Bewusst OHNE `auth.status`-Verzweigung (Codex-R3): der /me-Zustand ist hier
  // kein verlässliches Signal (siehe GlobalLogout-JSDoc). Immer abmelden.
  if (location === '/logout') {
    return <GlobalLogout />;
  }

  if (auth.status === 'unauthed') {
    // (b) Jeder Pfad unauthed → Login (tools hat keine public Route). Post-Login
    // reload() (nicht assign('/')) — path-agnostisch (Dev /index-tools.html, Prod
    // /) UND die Handoff-Params überleben in der URL → nach reload() greift (a).
    return (
      <LoginPage title="GREENFORCE Tools" onPasswordSuccess={() => window.location.reload()} />
    );
  }

  const handleLogout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('[auth] logout failed:', err);
    }
    setAuth({ status: 'unauthed' });
  };

  // Profil-Save (Must 5): tools-lokaler State-Refresh statt DAM-`AUTH_USER_CHANGED`
  // → AvatarMenu zeigt den neuen Namen sofort, ohne Reload.
  const handleProfileSaved = (displayName: string) => {
    setAuth((prev) =>
      prev.status === 'authed'
        ? { status: 'authed', user: { ...prev.user, displayName } }
        : prev,
    );
  };

  // (d) Forced-Change-Gate ZUERST: Parität zu DAM/Transfer + der Mint würde bei
  // mustChangePassword eh 403en (sso-handoff.ts). Die tools-Subdomain darf kein
  // Bypass für admin-gesetzte Temp-Passwörter sein (der tv-Bump wirkt eh
  // app-übergreifend via shared users-Tabelle). Nach dem Wechsel reload() → (a).
  if (auth.user.mustChangePassword) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-brand-cream p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
          <h1 className="text-2xl font-semibold text-brand-green-700 mb-2 tracking-tight">
            Passwort festlegen
          </h1>
          <p className="text-sm text-brand-green-600 mb-6">
            Dein Passwort wurde von einem Admin vergeben. Bitte lege jetzt ein eigenes fest, um
            fortzufahren.
          </p>
          <ChangePasswordForm forced onSuccess={() => window.location.reload()} />
          <button
            type="button"
            onClick={handleLogout}
            className="mt-6 text-xs text-brand-green-600 underline hover:text-brand-green-800"
          >
            Abmelden
          </button>
        </div>
      </main>
    );
  }

  // (a) authed + gültige Handoff-Params → sofortiger Auto-Handoff (pfad-unabhängig).
  if (handoff.app) {
    return <AutoHandoff app={handoff.app} next={handoff.next} />;
  }

  // (c) authed ohne Handoff-Params → geroutete tools-Shell (Launchpad + Settings,
  // PR-B). ToastProvider deckt die useToast-Nutzer (ToolsProfileForm + TeamPage) ab.
  // Team-Route ist admin-gated (Member → Redirect `/`); Backend `assertAdmin` bleibt
  // die echte Boundary. `/settings` → profile; unbekannter Pfad → `/` (Must 2).
  return (
    <ToastProvider>
      <Switch>
        <Route path="/">
          <Launchpad user={auth.user} onLogout={handleLogout} />
        </Route>
        <Route path="/settings/profile">
          <ToolsSettings
            user={auth.user}
            onLogout={handleLogout}
            onProfileSaved={handleProfileSaved}
            tab="profile"
          />
        </Route>
        <Route path="/settings/team">
          {auth.user.isAdmin ? (
            <ToolsSettings
              user={auth.user}
              onLogout={handleLogout}
              onProfileSaved={handleProfileSaved}
              tab="team"
            />
          ) : (
            <Redirect to="/" replace />
          )}
        </Route>
        <Route path="/settings">
          <Redirect to="/settings/profile" replace />
        </Route>
        <Route>
          <Redirect to="/" replace />
        </Route>
      </Switch>
    </ToastProvider>
  );
}
